import React, { useState, useEffect, useMemo } from 'react';
import { Sliders, RotateCcw, Check, Sparkles, Play, TrendingUp, ShieldCheck, Activity } from 'lucide-react';
import type { ModelParameters, BreadthSummary, MacroGateSummary } from '../types';
import {
  DEFAULT_MODEL_PARAMS,
  recalculateUniverse,
  type BacktestSimulationMetrics,
} from '../lib/recalculate';
import { FULL_UNIVERSE_STOCKS } from '../data/fullUniverseStocks';

interface MlStudioProps {
  initialParams?: ModelParameters;
  activeProfileName?: string;
  onApplyParams?: (params: ModelParameters) => void;
  liveBreadth?: BreadthSummary;
  liveMacro?: MacroGateSummary;
  liveBacktest?: BacktestSimulationMetrics;
}

export const MlStudio: React.FC<MlStudioProps> = ({
  initialParams,
  activeProfileName = 'Default Kostolany v1',
  onApplyParams,
  liveBreadth,
  liveMacro,
  liveBacktest,
}) => {
  const [params, setParams] = useState<ModelParameters>(initialParams ?? DEFAULT_MODEL_PARAMS);
  const [isSaved, setIsSaved] = useState(false);
  const [backtestRunCount, setBacktestRunCount] = useState(1);

  useEffect(() => {
    if (initialParams) {
      setParams(initialParams);
    }
  }, [initialParams]);

  // Live preview calculation whenever local sliders move
  const previewResult = useMemo(
    () => recalculateUniverse(FULL_UNIVERSE_STOCKS, params),
    [params]
  );

  const displayBreadth = liveBreadth && isSaved ? liveBreadth : previewResult.breadth;
  const displayMacro = liveMacro && isSaved ? liveMacro : previewResult.macro;
  const displayBacktest = liveBacktest && isSaved ? liveBacktest : previewResult.backtest;

  const handleChange = (key: keyof ModelParameters, val: number) => {
    const next = { ...params, [key]: val };
    setParams(next);
    setIsSaved(false);
  };

  const handleReset = () => {
    setParams(DEFAULT_MODEL_PARAMS);
    if (onApplyParams) onApplyParams(DEFAULT_MODEL_PARAMS);
    setIsSaved(false);
  };

  const handleSave = () => {
    if (onApplyParams) onApplyParams(params);
    setBacktestRunCount((c) => c + 1);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2500);
  };

  const handleRunBacktestNow = () => {
    if (onApplyParams) onApplyParams(params);
    setBacktestRunCount((c) => c + 1);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2500);
  };

  return (
    <div className="stitch-card rounded-xl p-4 sm:p-5 flex flex-col h-full space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm sm:text-base font-bold text-slate-100 flex flex-wrap items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#38bdf8]" />
            <span>參數與機器學習機制工作室</span>
            <span className="text-xs font-mono font-normal text-sky-400/85">
              (ML REGIME & HYPERPARAMETER STUDIO)
            </span>
            <span className="px-2 py-0.5 rounded text-[11px] font-mono bg-blue-950/70 text-terminal-cyan border border-blue-500/40">
              目前參數組: {activeProfileName}
            </span>
          </h2>
          <p className="text-xs text-slate-300/90 mt-0.5">
            動態微調科斯托蘭尼模型閥值向量 <span className="text-sky-300 font-mono">θ</span>，即時重算台股全市場 1,826 檔階段分類、共振訊號、4G 部位與 Walk-Forward 回測績效
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="studio-reset-btn"
            onClick={handleReset}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border border-[#1e293b] bg-[#0b0f17] text-xs font-mono text-slate-200 hover:bg-[#131a26] hover:text-white transition-all cursor-pointer"
          >
            <RotateCcw className="w-3.5 h-3.5 text-sky-400" />
            <span>重置預設值</span>
          </button>

          <button
            type="button"
            data-testid="studio-run-backtest-btn"
            onClick={handleRunBacktestNow}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-emerald-600/90 text-white text-xs font-mono font-semibold hover:bg-emerald-500 transition-all shadow-sm border border-emerald-400/40 cursor-pointer"
          >
            <Play className="w-3.5 h-3.5 fill-white" />
            <span>執行回測並套用全市場</span>
          </button>

          <button
            type="button"
            data-testid="studio-save-btn"
            onClick={handleSave}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-primary-container text-white text-xs font-mono font-semibold hover:bg-blue-600 transition-all shadow-sm border border-blue-400/40 cursor-pointer"
          >
            {isSaved ? <Check className="w-3.5 h-3.5" /> : <Sliders className="w-3.5 h-3.5" />}
            <span>{isSaved ? '已套用至 D1 與全站' : '儲存並同步至 D1'}</span>
          </button>
        </div>
      </div>

      {/* Live Walk-Forward Backtest & Model Recalculation Scorecard */}
      <div
        data-testid="backtest-scorecard"
        className="bg-[#0b0f17]/90 border border-[#1e293b] rounded-xl p-3.5 space-y-3"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-terminal-cyan" />
            <span className="text-xs sm:text-sm font-bold text-slate-100">
              Walk-Forward 回測與全市場 1,826 檔即時重算報告
            </span>
            <span className="text-[11px] font-mono text-sky-400/85">
              (2021–2026 滾動樣本外回測 | Run #{backtestRunCount})
            </span>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-mono">
            <span className="px-2 py-0.5 rounded bg-sky-950/70 text-sky-300 border border-sky-500/30">
              重分類變動標的: <strong data-testid="backtest-reclassified">{displayBacktest.reclassified_count}</strong> 檔
            </span>
            <span className="px-2 py-0.5 rounded bg-emerald-950/60 text-emerald-300 border border-emerald-500/30">
              宏觀定錨: {displayMacro.macro_phase} ({displayMacro.macro_regime})
            </span>
          </div>
        </div>

        {/* 6 Key Backtest Performance Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 font-mono">
          <div className="p-2.5 rounded-lg bg-[#111723] border border-[#1c2638]">
            <div className="text-[10px] text-slate-400">策略年化報酬 (CAGR)</div>
            <div data-testid="backtest-cagr" className="text-sm font-bold text-rose-400 mt-0.5">
              +{displayBacktest.cagr_pct.toFixed(2)}%
            </div>
            <div className="text-[10px] text-sky-400/85 mt-0.5">
              超額 Alpha: +{displayBacktest.alpha_pct.toFixed(2)}%
            </div>
          </div>

          <div className="p-2.5 rounded-lg bg-[#111723] border border-[#1c2638]">
            <div className="text-[10px] text-slate-400">夏普比率 (Sharpe)</div>
            <div data-testid="backtest-sharpe" className="text-sm font-bold text-terminal-cyan mt-0.5">
              {displayBacktest.sharpe_ratio.toFixed(2)}
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              Sortino: {displayBacktest.sortino_ratio.toFixed(2)}
            </div>
          </div>

          <div className="p-2.5 rounded-lg bg-[#111723] border border-[#1c2638]">
            <div className="text-[10px] text-slate-400">最大回撤 (Max DD)</div>
            <div data-testid="backtest-mdd" className="text-sm font-bold text-emerald-400 mt-0.5">
              {displayBacktest.max_drawdown_pct.toFixed(2)}%
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              大盤同期: -28.60%
            </div>
          </div>

          <div className="p-2.5 rounded-lg bg-[#111723] border border-[#1c2638]">
            <div className="text-[10px] text-slate-400">波段勝率 (Win Rate)</div>
            <div data-testid="backtest-winrate" className="text-sm font-bold text-amber-300 mt-0.5">
              {displayBacktest.win_rate_pct.toFixed(1)}%
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              獲利因子: {displayBacktest.profit_factor.toFixed(2)}
            </div>
          </div>

          <div className="p-2.5 rounded-lg bg-[#111723] border border-[#1c2638]">
            <div className="text-[10px] text-slate-400">買進區佔比 (B3+A1)</div>
            <div data-testid="backtest-buy-pct" className="text-sm font-bold text-sky-300 mt-0.5">
              {(displayBreadth.pct_buy_zone * 100).toFixed(1)}%
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              共 {displayBacktest.active_long_candidates} 檔吸籌標的
            </div>
          </div>

          <div className="p-2.5 rounded-lg bg-[#111723] border border-[#1c2638]">
            <div className="text-[10px] text-slate-400">總權益上限 / 乘數</div>
            <div className="text-sm font-bold text-slate-100 mt-0.5">
              {(displayMacro.max_equity_cap * 100).toFixed(0)}% / {displayMacro.macro_sizing_multiplier.toFixed(2)}x
            </div>
            <div className="text-[10px] text-sky-400/85 mt-0.5">
              樣本交易數: {displayBacktest.total_trades} 筆
            </div>
          </div>
        </div>

        {/* Live 6-Phase Distribution Bar */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-[11px] font-mono text-slate-300">
          <div className="flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5 text-sky-400" />
            <span>重算後六階段分布 (A1→A2→A3→B1→B2→B3):</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(['A1', 'A2', 'A3', 'B1', 'B2', 'B3'] as const).map((p) => (
              <span
                key={p}
                className="px-2 py-0.5 rounded bg-[#141b28] border border-[#1e293b] text-slate-200"
              >
                <strong className="text-sky-300">{p}:</strong> {displayBreadth.phase_counts[p]}檔 (
                {(displayBreadth.phase_pcts[p] * 100).toFixed(1)}%)
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Sliders Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 overflow-y-auto max-h-[580px] pr-1">
        {/* Group 1: Moving Averages & Volume Windows */}
        <div className="bg-[#0b0f17]/85 border border-[#182232] rounded-xl p-3.5 space-y-3.5">
          <h3 className="text-xs font-bold text-slate-100 flex items-center justify-between bg-[#131a26]/80 px-2.5 py-1.5 rounded-lg">
            <span>基礎均線與波動度天期</span>
            <span className="text-[10px] text-sky-400/90 font-mono">CORE_WINDOWS</span>
          </h3>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300">MA 短期均線 (月線)</span>
              <span data-testid="threshold-value-ma_short" className="font-mono font-bold text-sky-300">{params.ma_short} 日</span>
            </div>
            <input
              data-testid="threshold-slider-ma_short"
              type="range"
              min={5}
              max={40}
              step={1}
              value={params.ma_short}
              onChange={(e) => handleChange('ma_short', Number(e.target.value))}
              className="w-full accent-sky-400"
            />
          </div>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300">MA 中期均線 (季線)</span>
              <span data-testid="threshold-value-ma_mid" className="font-mono font-bold text-sky-300">{params.ma_mid} 日</span>
            </div>
            <input
              data-testid="threshold-slider-ma_mid"
              type="range"
              min={40}
              max={100}
              step={5}
              value={params.ma_mid}
              onChange={(e) => handleChange('ma_mid', Number(e.target.value))}
              className="w-full accent-sky-400"
            />
          </div>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300">MA 長期基準 (年線 / 主人)</span>
              <span data-testid="threshold-value-ma_long" className="font-mono font-bold text-sky-300">{params.ma_long} 日</span>
            </div>
            <input
              data-testid="threshold-slider-ma_long"
              type="range"
              min={150}
              max={300}
              step={10}
              value={params.ma_long}
              onChange={(e) => handleChange('ma_long', Number(e.target.value))}
              className="w-full accent-sky-400"
            />
          </div>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300">ATR 真實波幅週期</span>
              <span data-testid="threshold-value-atr_period" className="font-mono font-bold text-sky-300">{params.atr_period} 日</span>
            </div>
            <input
              data-testid="threshold-slider-atr_period"
              type="range"
              min={10}
              max={30}
              step={1}
              value={params.atr_period}
              onChange={(e) => handleChange('atr_period', Number(e.target.value))}
              className="w-full accent-sky-400"
            />
          </div>
        </div>

        {/* Group 2: Dog Bias & Valuation Ladder Thresholds */}
        <div className="bg-[#0b0f17]/85 border border-[#182232] rounded-xl p-3.5 space-y-3.5">
          <h3 className="text-xs font-bold text-slate-100 flex items-center justify-between bg-[#131a26]/80 px-2.5 py-1.5 rounded-lg">
            <span>主人與狗 (MA200 乖離閥值)</span>
            <span className="text-[10px] text-sky-400/90 font-mono">DOG_BIAS</span>
          </h3>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300">A3 狂熱膨脹閥值 (階梯 5)</span>
              <span data-testid="threshold-value-dog_euphoria_min" className="font-mono font-bold text-amber-300">+{(params.dog_euphoria_min * 100).toFixed(0)}%</span>
            </div>
            <input
              data-testid="threshold-slider-dog_euphoria_min"
              type="range"
              min={0.20}
              max={0.60}
              step={0.05}
              value={params.dog_euphoria_min}
              onChange={(e) => handleChange('dog_euphoria_min', Number(e.target.value))}
              className="w-full accent-amber-400"
            />
          </div>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300">A2 突破基準門檻 (階梯 4)</span>
              <span data-testid="threshold-value-dog_breakout_min" className="font-mono font-bold text-sky-300">+{(params.dog_breakout_min * 100).toFixed(0)}%</span>
            </div>
            <input
              data-testid="threshold-slider-dog_breakout_min"
              type="range"
              min={0.01}
              max={0.15}
              step={0.01}
              value={params.dog_breakout_min}
              onChange={(e) => handleChange('dog_breakout_min', Number(e.target.value))}
              className="w-full accent-sky-400"
            />
          </div>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300">B3 恐慌超跌閥值 (階梯 -1)</span>
              <span data-testid="threshold-value-dog_panic_bottom" className="font-mono font-bold text-emerald-300">{(params.dog_panic_bottom * 100).toFixed(0)}%</span>
            </div>
            <input
              data-testid="threshold-slider-dog_panic_bottom"
              type="range"
              min={-0.45}
              max={-0.10}
              step={0.05}
              value={params.dog_panic_bottom}
              onChange={(e) => handleChange('dog_panic_bottom', Number(e.target.value))}
              className="w-full accent-emerald-400"
            />
          </div>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300">B1 頭部極端做頭閥值</span>
              <span data-testid="threshold-value-dog_climax_top" className="font-mono font-bold text-rose-300">+{(params.dog_climax_top * 100).toFixed(0)}%</span>
            </div>
            <input
              data-testid="threshold-slider-dog_climax_top"
              type="range"
              min={0.25}
              max={0.60}
              step={0.05}
              value={params.dog_climax_top}
              onChange={(e) => handleChange('dog_climax_top', Number(e.target.value))}
              className="w-full accent-rose-400"
            />
          </div>
        </div>

        {/* Group 3: Volume Ratios & Macro Regimes */}
        <div className="bg-[#0b0f17]/85 border border-[#182232] rounded-xl p-3.5 space-y-3.5">
          <h3 className="text-xs font-bold text-slate-100 flex items-center justify-between bg-[#131a26]/80 px-2.5 py-1.5 rounded-lg">
            <span>成交量比與大盤濾網</span>
            <span className="text-[10px] text-sky-400/90 font-mono">VR_MACRO</span>
          </h3>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300">A1 築底量枯竭閥值 (VR60)</span>
              <span data-testid="threshold-value-vr_volume_dry" className="font-mono font-bold text-sky-300">{params.vr_volume_dry.toFixed(2)}x</span>
            </div>
            <input
              data-testid="threshold-slider-vr_volume_dry"
              type="range"
              min={0.4}
              max={1.0}
              step={0.05}
              value={params.vr_volume_dry}
              onChange={(e) => handleChange('vr_volume_dry', Number(e.target.value))}
              className="w-full accent-sky-400"
            />
          </div>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300">A2 放量突破門檻 (VR60)</span>
              <span data-testid="threshold-value-vr_volume_surge" className="font-mono font-bold text-sky-300">{params.vr_volume_surge.toFixed(2)}x</span>
            </div>
            <input
              data-testid="threshold-slider-vr_volume_surge"
              type="range"
              min={1.0}
              max={2.0}
              step={0.05}
              value={params.vr_volume_surge}
              onChange={(e) => handleChange('vr_volume_surge', Number(e.target.value))}
              className="w-full accent-sky-400"
            />
          </div>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300">大盤多頭牛市廣度門檻 (BULL_ZONE)</span>
              <span data-testid="threshold-value-macro_bull_zone_min" className="font-mono font-bold text-emerald-300">{(params.macro_bull_zone_min * 100).toFixed(0)}%</span>
            </div>
            <input
              data-testid="threshold-slider-macro_bull_zone_min"
              type="range"
              min={0.40}
              max={0.70}
              step={0.05}
              value={params.macro_bull_zone_min}
              onChange={(e) => handleChange('macro_bull_zone_min', Number(e.target.value))}
              className="w-full accent-emerald-400"
            />
          </div>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300">臺指期恐慌逆價差閥值</span>
              <span data-testid="threshold-value-macro_futures_panic_basis" className="font-mono font-bold text-rose-300">{params.macro_futures_panic_basis.toFixed(0)} 點</span>
            </div>
            <input
              data-testid="threshold-slider-macro_futures_panic_basis"
              type="range"
              min={-300}
              max={-50}
              step={10}
              value={params.macro_futures_panic_basis}
              onChange={(e) => handleChange('macro_futures_panic_basis', Number(e.target.value))}
              className="w-full accent-rose-400"
            />
          </div>
        </div>
      </div>

      {/* Execution Pipeline Explanation Banner */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5 rounded-lg bg-[#0b0f17] border border-[#182232] text-xs font-mono text-slate-300">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>
            引擎說明：拖曳上方滑桿會即時預覽重算結果；點擊「執行回測並套用全市場」或「儲存並同步至 D1」將同步更新全站雞蛋圖、板塊熱力圖與個股掃描表。
          </span>
        </div>
      </div>
    </div>
  );
};
